import { useEffect, useMemo, useRef, useState } from "react";

import { useObjectiveById, useLookupsObjective, useSaveObjective } from "./useObjective.js";
import { toast } from "react-toastify";

export function useObjectiveForm({ option, filters, elementId, baseObject, onClose }) {
	const isOpen = option === "crear" || option === "editar";
	const isEditMode = option === "editar";
	const modalTitle = useMemo(() => (isEditMode ? "Editar Objetivo OA" : "Crear Objetivo OA"), [isEditMode]);

	const { mutate: saveObjective, isLoading: isLoadingSave } = useSaveObjective();
	const { data: lookups, isLoading: isLoadingLookups } = useLookupsObjective();

	const canLoadDetail = isEditMode && !!elementId && !!lookups?.estates_parents && !!lookups?.estates;
	const { data: element, isLoading: isLoadingElement, isFetching: isFetchingElement } = useObjectiveById(filters, elementId, canLoadDetail);

	const [formState, setFormState] = useState({ ...baseObject });
	const [formErrors, setFormErrors] = useState({});

	const estateParentOptions = useMemo(() => lookups?.estates_parents ?? [], [lookups]);
	const estateOptions = useMemo(() => lookups?.estates ?? [], [lookups]);

	const estatesByParentMap = useMemo(() => {
		const map = new Map();
		for (const estate of estateOptions) {
			if (!map.has(estate.estate_parent_id)) map.set(estate.estate_parent_id, []);
			map.get(estate.estate_parent_id).push(estate);
		}
		return map;
	}, [estateOptions]);

	const initAutoSelectDoneRef = useRef(false);

	useEffect(() => {
		if (!isOpen || !lookups || isLoadingLookups || initAutoSelectDoneRef.current) return;
		setFormState((previous) => {
			const next = { ...previous };

			const hasNoParents = !Array.isArray(next.estate_parent_id) || next.estate_parent_id.length === 0;
			if (hasNoParents && estateParentOptions.length === 1) {
				const onlyParentId = estateParentOptions[0].value;
				next.estate_parent_id = [onlyParentId];

				const estatesForOnlyParent = estatesByParentMap.get(onlyParentId) || [];
				const hasNoEstates = !Array.isArray(next.estate_id) || next.estate_id.length === 0;
				if (hasNoEstates && estatesForOnlyParent.length === 1) {
					next.estate_id = [estatesForOnlyParent[0].value];
				}
			}

			return next;
		});
		initAutoSelectDoneRef.current = true;
	}, [isOpen, lookups, isLoadingLookups, estateParentOptions, estatesByParentMap]);

	const handleEstateParentsChange = (selectedParentIds) => {
		const nextSelectedParentIds = selectedParentIds || [];

		const validEstateIdSet = new Set(nextSelectedParentIds.flatMap((parentId) => (estatesByParentMap.get(parentId) || []).map((estate) => estate.value)));

		const previousSelectedEstateIds = Array.isArray(formState.estate_id) ? formState.estate_id : [];
		const prunedSelectedEstateIds = previousSelectedEstateIds.filter((id) => validEstateIdSet.has(id));

		let nextSelectedEstateIds = prunedSelectedEstateIds;
		if (prunedSelectedEstateIds.length === 0) {
			const allValidEstateIds = Array.from(validEstateIdSet);
			nextSelectedEstateIds = allValidEstateIds.length === 1 ? [allValidEstateIds[0]] : [];
		}

		setFormState((previous) => ({
			...previous,
			estate_parent_id: nextSelectedParentIds,
			estate_id: nextSelectedEstateIds,
		}));

		setFormErrors((previousErrors) => {
			const nextErrors = { ...previousErrors };
			delete nextErrors.estate_parent_id;
			delete nextErrors.estate_id;
			return nextErrors;
		});
	};

	const filteredEstateOptions = useMemo(() => {
		const selectedParentIds = formState.estate_parent_id || [];
		if (!selectedParentIds.length) return [];
		const seen = new Set();
		const merged = [];
		for (const parentId of selectedParentIds) {
			for (const estate of estatesByParentMap.get(parentId) || []) {
				if (!seen.has(estate.value)) {
					seen.add(estate.value);
					merged.push(estate);
				}
			}
		}
		return merged;
	}, [formState.estate_parent_id, estatesByParentMap]);

	useEffect(() => {
		if (isEditMode && element) {
			const uniqueEstatesParents = Array.from(new Set((element.estates_parents || []).map((v) => Number(v.value))));
			const uniqueEstates = Array.from(new Set((element.estates || []).map((v) => Number(v.value))));

			setFormState((prev) => ({
				...prev,
				...element,
				estate_parent_id: uniqueEstatesParents ?? [],
				estate_id: uniqueEstates ?? [],
			}));
		}
	}, [isEditMode, element]);

	const validateForm = (state) => {
		const next = {};
		if (!Array.isArray(state.estate_parent_id) || state.estate_parent_id.length === 0) next.estate_parent_id = "Requerido";
		if (!Array.isArray(state.estate_id) || state.estate_id.length === 0) next.estate_id = "Requerido";
		if (!state.name?.trim()) next.name = "Requerido";
		return next;
	};

	const buildPayload = (state) => {
		const uniqueParents = Array.from(new Set((state.estate_parent_id || []).map(Number)));
		const uniqueEstates = Array.from(new Set((state.estate_id || []).map(Number)));

		return {
			id: isEditMode ? Number(elementId) : undefined,
			filters: filters,
			form: {
				estate_parent_id: uniqueParents,
				estate_id: uniqueEstates,
				name: state.name?.trim() || "",
			},
		};
	};

	const handleSubmit = async (event) => {
		event.preventDefault();

		const nextErrors = validateForm(formState);
		setFormErrors(nextErrors);
		if (Object.keys(nextErrors).length) return;

		const payload = buildPayload(formState);

		saveObjective(payload, {
			onSuccess: () => {
				toast.success(isEditMode ? "Objetivo OA actualizado" : "Objetivo OA creado");
				onClose();
			},
			onError: (error) => {
				toast.error(<div style={{ whiteSpace: "pre-line" }}>{error?.response?.data?.message || "Error al guardar el Objetivo OA"}</div>);
			},
		});
	};

	return {
		isOpen,
		isEditMode,
		modalTitle,
		isLoadingSave,
		isLoadingElement,
		isFetchingElement,

		estateParentOptions,
		filteredEstateOptions,

		formState,
		setFormState,
		formErrors,
		setFormErrors,

		handleEstateParentsChange,
		handleSubmit,
	};
}
